/** Session state shared by every part of the SPA.
 *
 *  Split out of auth.ts so the request helpers (csrfFetch) can depend on it
 *  without pulling React in, which keeps them testable under `node --test`. */

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

export interface AuthState {
  loading: boolean;
  authenticated: boolean;
  /** Not signed in as far as we know, because the server could not be
   *  asked (offline, or the server down): not a signed-out session. */
  unreachable?: boolean;
  user: AuthUser | null;
  csrfToken: string | null;
}

let cached: AuthState = { loading: true, authenticated: false, user: null, csrfToken: null };
const listeners = new Set<(s: AuthState) => void>();

function publish(state: AuthState) {
  cached = state;
  for (const l of listeners) l(state);
}

export function getAuthSnapshot(): AuthState {
  return cached;
}

export function subscribeAuth(listener: (s: AuthState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Fetch /auth/me.
 *
 *  **A failed revalidation does not sign you out.** This runs on every window
 *  focus, so a single 502 from the tunnel, or a daemon busy on Outlook's COM
 *  thread, used to flip the whole app to "signed out" — which unmounts
 *  everything under AuthGate. When the next check succeeded the app came back
 *  freshly mounted, having silently thrown away the open folder and the
 *  selected message. From the outside that looks like the app randomly losing
 *  your place a few seconds after you clicked something.
 *
 *  So a transient failure now KEEPS the session we already had. Only a
 *  definitive 401 or 403 — the server saying "you are not signed in", not "I
 *  could not answer" — ends it.
 *
 *  On the very first load there is nothing to keep, and showing the sign-in
 *  screen is the right answer for an unknown state. */
export async function refreshAuth(): Promise<AuthState> {
  const wasAuthenticated = cached.authenticated;
  try {
    const res = await fetch("/auth/me", { credentials: "same-origin" });
    if (!res.ok) {
      const definitive = res.status === 401 || res.status === 403;
      if (!definitive && wasAuthenticated) {
        // Could not answer. Keep what we had rather than tearing the app down.
        const kept: AuthState = { ...cached, loading: false };
        publish(kept);
        return kept;
      }
      if (!definitive) {
        // No answer about the session (the server is down): say that, not
        // "sign in".
        const next: AuthState = { loading: false, authenticated: false, user: null, csrfToken: null, unreachable: true };
        publish(next);
        return next;
      }
      const next: AuthState = {
        loading: false,
        authenticated: false,
        user: null,
        csrfToken: null,
      };
      publish(next);
      return next;
    }
    const body = (await res.json()) as { authenticated?: boolean; user?: AuthUser; csrfToken?: string };
    const next: AuthState = {
      loading: false,
      authenticated: !!body.authenticated,
      user: body.user || null,
      csrfToken: body.csrfToken || null,
    };
    publish(next);
    return next;
  } catch {
    // Network error — offline, tunnel blip, DNS. Says nothing about whether
    // the session is valid, so it must not end one.
    if (wasAuthenticated) {
      const kept: AuthState = { ...cached, loading: false };
      publish(kept);
      return kept;
    }
    const next: AuthState = { loading: false, authenticated: false, user: null, csrfToken: null, unreachable: true };
    publish(next);
    return next;
  }
}

export function getCsrfToken(): string | null {
  return cached.csrfToken;
}
